// Demo Mode controller: the one runtime authority for whether the browser mask
// is off, activating, ready or failed. State lives in the `demoMode` Vuex
// module so components, charts and plain helpers read the same value.
// localStorage and sessionStorage are persistence only; neither is read to
// decide the current state after boot.
//
// Cross-tab semantics:
//   - Turning Demo Mode ON anywhere protects every open tab (a remote ON
//     activates this tab).
//   - Turning it OFF is per tab. A remote OFF never unmasks a tab that is
//     protecting, and the tab keeps a sessionStorage flag so a reload of that
//     tab comes back protected.
//   - While any tab is protecting, every new tab starts protected. A tab where
//     the presenter explicitly turned Demo Mode off stays off on reload. Each
//     protecting tab holds its own expiring lease key in localStorage; boot
//     reads the leases synchronously, so there is no unprotected first paint
//     and no wait.
//
// Who may use it: superusers only. Until the signed-in user is known (boot,
// or storage that cannot be read) Demo Mode fails closed and may be on. Once
// an authenticated user is known and is not a superuser, Demo Mode is turned
// off, the shared flag, this tab's session flag and every tab's lease are
// cleared, and it cannot be entered again (a remote ON from another tab
// included). Logging out clears the same state. Session expiry is not a
// logout: a presenter whose session lapses mid-demo comes back protected.
//
// Integration seam for a server-authoritative protected session (PR 3): the
// server session would become another source for requestDemoMode()/boot and
// could veto exitDemoMode(); nothing here assumes such an API exists today.

import { effectScope, nextTick } from 'vue';
import * as Sentry from '@sentry/vue';
import { useToast } from 'vue-toastification';
import store from '@/store';
import { DEMO_STATUS } from '@/store/modules/demoMode';
import { COMPANY } from '@/constants/company';
import {
  activateSpoof,
  deactivateSpoof,
  entitySpoofKind,
  seedDemoOrgNames,
  setSensitiveHrefRewriter,
  setSpoofFailureHandler,
} from '@/utils/demoSpoof';
import { clearBootRecord, clearRouteTokens, setRouteTokenAccess, tokenizeHref } from '@/utils/demoRoutes';

export const DEMO_STORAGE_KEY = 'demoMode';
const RECENT_ENTITIES_KEY_PREFIX = 'entitySearch.recentEntities';
export const DEMO_SESSION_KEY = 'demoModeSession';
export const DEMO_LEASE_PREFIX = 'demoModeLease:';
// Hidden tabs can have timers throttled to once a minute, so the lease outlives
// that with margin. A tab that dies without pagehide leaves its lease for at
// most this long, which only errs toward protecting new pages.
export const DEMO_LEASE_TTL_MS = 3 * 60 * 1000;
export const DEMO_LEASE_REFRESH_MS = 20 * 1000;

const tabId = Math.random().toString(36).slice(2) + Date.now().toString(36);
let leaseTimer = null;

let initialized = false;
let coveredCount = 0;
let activationStep = null;
let exitStep = null;

// An async step that must finish before activation reports ready, e.g. the
// router moving a patient page onto its stand-in token (registered by main.js,
// which owns the router). If it throws, activation fails closed.
export function setDemoActivationStep(fn) {
  activationStep = typeof fn === 'function' ? fn : null;
}

// Runs synchronously once this tab is off, before the route token table can
// be cleared: e.g. the router putting a tokenized patient page back on its
// real path. A failure is swallowed; the tab is already unmasked.
export function setDemoExitStep(fn) {
  exitStep = typeof fn === 'function' ? fn : null;
}

// Route tokens stand in for real identifiers in URLs while any tab protects.
// The table is dropped once no tab is protecting.
function clearRouteTokensIfUnused() {
  let otherTabProtects = true;
  try {
    otherTabProtects = hasLiveLease();
  } catch {
    /* storage unreadable: keep the table; it holds nothing new */
  }
  if (!otherTabProtects) clearRouteTokens();
}

function status() {
  return store.getters['demoMode/status'];
}

function generation() {
  return store.getters['demoMode/generation'];
}

function leaseKey() {
  return DEMO_LEASE_PREFIX + tabId;
}

function hasLiveLease(now = Date.now()) {
  for (let i = 0; i < localStorage.length; i += 1) {
    const key = localStorage.key(i);
    if (!key || !key.startsWith(DEMO_LEASE_PREFIX) || key === leaseKey()) continue;
    const expires = Number(localStorage.getItem(key));
    if (Number.isFinite(expires) && expires > now) return true;
    localStorage.removeItem(key);
    i -= 1;
  }
  return false;
}

function writeLease() {
  try {
    localStorage.setItem(leaseKey(), String(Date.now() + DEMO_LEASE_TTL_MS));
  } catch {
    /* persistence failure is already surfaced through persist() */
  }
}

function releaseLease() {
  try {
    localStorage.removeItem(leaseKey());
  } catch {
    /* nothing to release */
  }
}

function holdLease() {
  writeLease();
  if (leaseTimer === null) leaseTimer = setInterval(writeLease, DEMO_LEASE_REFRESH_MS);
}

function dropLease() {
  if (leaseTimer !== null) {
    clearInterval(leaseTimer);
    leaseTimer = null;
  }
  releaseLease();
}

function onPageHide() {
  releaseLease();
}

function onPageShow(event) {
  if (event.persisted && status() !== DEMO_STATUS.OFF) writeLease();
}

// true: a superuser is signed in. false: an authenticated user who is not a
// superuser. null: not known yet, or nobody signed in.
export function demoModeAllowedForCurrentUser() {
  if (!store.getters['auth/isAuthenticated']) return null;
  const user = store.getters['auth/current_user'];
  if (!user) return null;
  return user.is_superuser === true;
}

// Removes every persisted trace of Demo Mode this browser holds: the shared
// flag, this tab's session flag and boot record, every tab's lease and the
// route token table. Never throws.
function clearPersistedDemoMode() {
  clearRouteTokens();
  clearBootRecord();
  try {
    localStorage.removeItem(DEMO_STORAGE_KEY);
    for (let i = localStorage.length - 1; i >= 0; i -= 1) {
      const key = localStorage.key(i);
      if (key && key.startsWith(DEMO_LEASE_PREFIX)) localStorage.removeItem(key);
    }
  } catch {
    /* unreadable storage: nothing persisted can be cleared */
  }
  try {
    sessionStorage.removeItem(DEMO_SESSION_KEY);
  } catch {
    /* as above */
  }
}

// Turns Demo Mode off and clears its persisted state: for a signed-in user who
// is not a superuser, and at logout.
function revokeDemoMode() {
  exitDemoMode();
  clearPersistedDemoMode();
}

function enforceDemoModeAccess() {
  if (demoModeAllowedForCurrentUser() === false) revokeDemoMode();
}

// Reads the persisted flag and other tabs' leases without ever throwing.
// Storage that cannot be read cannot prove Demo Mode is off, so it fails closed.
export function readPersistedDemoFlag() {
  try {
    if (sessionStorage.getItem(DEMO_SESSION_KEY) === 'false') return false;
  } catch {
    return true;
  }
  try {
    if (localStorage.getItem(DEMO_STORAGE_KEY) === 'true') return true;
    if (hasLiveLease()) return true;
  } catch {
    return true;
  }
  try {
    if (sessionStorage.getItem(DEMO_SESSION_KEY) === 'true') return true;
  } catch {
    return true;
  }
  return false;
}

function persist({ shared, on }) {
  let ok = true;
  if (shared) {
    try {
      localStorage.setItem(DEMO_STORAGE_KEY, on ? 'true' : 'false');
    } catch {
      ok = false;
    }
  }
  try {
    if (on) sessionStorage.setItem(DEMO_SESSION_KEY, 'true');
    else sessionStorage.setItem(DEMO_SESSION_KEY, 'false');
  } catch {
    ok = false;
  }
  store.commit('demoMode/SET_PERSIST_FAILED', !ok);
}

// The browser tab title is outside the DOM mask and is written by several
// components (a patient's name on the patient page, agent page and ticket
// titles). While Demo Mode protects, its content is held at the neutral
// product name; the title from before entry comes back on exit.
//
// The guard must be idempotent with the other title writers or they fight:
// PhoneStatusIndicator re-prefixes a phone-status emoji whenever the title
// changes, and PhoneCallFloater flashes a fixed call banner. So the guard
// keeps a known status prefix and the call banner, and neutralizes only the
// rest; anything those writers then produce is already acceptable, and every
// writer reaches a fixed point instead of looping.
//
// The title restored on exit is the latest route title (setRouteTitle), not
// the one from entry: a boot with a leftover Demo Mode state starts the guard
// before the router writes the first route title.
const TITLE_STATUS_PREFIX_RE = /^((?:\u{1F4DE}|\u{1F7E2})\s)?([\s\S]*)$/u;
const TITLE_ALLOWED_BODIES = new Set([COMPANY.NAME, 'PHONE CALL ACTIVE']);
let titleObserver = null;
let savedTitle = null;

export function neutralTitleFor(title) {
  const [, prefix = '', body] = String(title ?? '').match(TITLE_STATUS_PREFIX_RE);
  if (TITLE_ALLOWED_BODIES.has(body)) return `${prefix}${body}`;
  return `${prefix}${COMPANY.NAME}`;
}

function holdNeutralTitle() {
  const neutral = neutralTitleFor(document.title);
  if (document.title !== neutral) document.title = neutral;
}

// The router's per-route title (router/index.js). While guarded it becomes the
// title restored on exit; any other write (a patient page's late response, a
// ticket name) is only neutralized, so it never comes back after exit.
export function setRouteTitle(title) {
  if (titleObserver) savedTitle = title;
  document.title = title;
}

function startTitleGuard() {
  if (titleObserver) return;
  savedTitle = document.title;
  holdNeutralTitle();
  titleObserver = new MutationObserver(holdNeutralTitle);
  titleObserver.observe(document.head, { subtree: true, childList: true, characterData: true });
}

function stopTitleGuard() {
  if (!titleObserver) return;
  titleObserver.disconnect();
  titleObserver = null;
  if (savedTitle && document.title === neutralTitleFor(document.title)) document.title = savedTitle;
  savedTitle = null;
}

function stopSessionReplay() {
  // Sentry replay records the live DOM; stop it before masking begins. It
  // cannot restart in-session, which is the safe direction.
  try {
    Sentry.getReplay()?.stop();
  } catch {
    /* replay not active */
  }
}

function afterPaint() {
  return new Promise((resolve) => {
    if (typeof requestAnimationFrame !== 'function') {
      setTimeout(resolve, 0);
      return;
    }
    requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
  });
}

function onEngineFailure({ stage, count }) {
  if (status() === DEMO_STATUS.OFF) return;
  if (stage === 'element') {
    coveredCount += count;
    store.commit('demoMode/SET_COVERED_COUNT', coveredCount);
    return;
  }
  fail();
}

// Masking could not start or stopped working. There is no cover: Demo Mode
// turns off and the presenter is told plainly that the screen is not masked.
function fail() {
  exitDemoMode();
  useToast().error("Demo Mode failed and is now off. Your screen is NOT masked.", { timeout: false });
}

// source: 'user' (the presenter's toggle), 'boot' (persisted flag at load),
// 'remote' (another tab turned it on). Only 'user' writes the shared flag, so
// a reload or a follower tab never re-broadcasts ON to tabs that turned it off.
function knownOrgNames() {
  const slug = store.getters['tenant/current_tenant_slug'];
  const entities = [];
  try {
    const recents = JSON.parse(localStorage.getItem(`${RECENT_ENTITIES_KEY_PREFIX}.${slug}`) || '[]');
    for (const recent of Array.isArray(recents) ? recents : []) {
      entities.push({ name: recent?.name, kind: entitySpoofKind(recent?.entity_type_name, recent?.name) });
      for (const ancestor of recent?.breadcrumb || []) {
        entities.push({ name: ancestor?.name, kind: entitySpoofKind(ancestor?.type_name, ancestor?.name) });
      }
    }
  } catch {
    entities.length = 0;
  }
  return { tenant: store.getters['tenant/current_tenant_name'] || null, entities };
}

export async function enterDemoMode({ source = 'user' } = {}) {
  const current = status();
  if (current === DEMO_STATUS.ACTIVATING || current === DEMO_STATUS.READY) return;
  if (demoModeAllowedForCurrentUser() === false) return;

  if (current === DEMO_STATUS.FAILED) deactivateSpoof();
  store.commit('demoMode/NEXT_GENERATION');
  const gen = generation();
  coveredCount = 0;
  store.commit('demoMode/SET_COVERED_COUNT', 0);
  store.commit('demoMode/SET_REMOTE_EXIT_IGNORED', false);
  store.commit('demoMode/SET_STATUS', DEMO_STATUS.ACTIVATING);
  // A presenter starting a session with no other tab protecting starts a
  // fresh token table (a reload or a follower tab keeps the shared one).
  if (source === 'user') clearRouteTokensIfUnused();
  persist({ shared: source === 'user', on: true });
  holdLease();
  startTitleGuard();
  document.body.classList.add('demo-mode');
  stopSessionReplay();

  // Let components re-render against the masking state (chart labels switch to
  // their spoofed forms) before the DOM pass.
  await nextTick();
  if (gen !== generation()) return;

  seedDemoOrgNames(knownOrgNames());
  const result = activateSpoof();
  if (!result.ok) {
    fail();
    return;
  }

  if (activationStep) {
    try {
      await activationStep();
    } catch {
      if (gen === generation()) fail();
      return;
    }
    if (gen !== generation()) return;
  }

  // Charts draw in watchers and effects after the state change; wait for them
  // and a paint before reporting ready.
  await nextTick();
  await afterPaint();
  if (gen !== generation()) return;
  if (status() !== DEMO_STATUS.ACTIVATING) return;
  store.commit('demoMode/SET_STATUS', DEMO_STATUS.READY);
}

export function exitDemoMode() {
  if (status() === DEMO_STATUS.OFF) return;
  clearBootRecord();
  store.commit('demoMode/NEXT_GENERATION');
  try {
    deactivateSpoof();
  } catch {
    /* the engine could not restore everything; Demo Mode still turns off */
  }
  document.body.classList.remove('demo-mode');
  persist({ shared: true, on: false });
  dropLease();
  stopTitleGuard();
  coveredCount = 0;
  store.commit('demoMode/SET_COVERED_COUNT', 0);
  store.commit('demoMode/SET_REMOTE_EXIT_IGNORED', false);
  store.commit('demoMode/SET_STATUS', DEMO_STATUS.OFF);
  if (exitStep) {
    try {
      exitStep();
    } catch {
      /* the tab is unmasked either way */
    }
  }
  clearRouteTokensIfUnused();
}

export function toggleDemoMode() {
  if (status() === DEMO_STATUS.OFF) return enterDemoMode({ source: 'user' });
  exitDemoMode();
  return Promise.resolve();
}

export function handleStorageEvent(event) {
  if (event.key !== DEMO_STORAGE_KEY && event.key !== null) return;
  if (typeof localStorage !== 'undefined' && event.storageArea && event.storageArea !== localStorage) return;
  const remoteOn = event.key === DEMO_STORAGE_KEY && event.newValue === 'true';
  const current = status();
  if (remoteOn) {
    // enterDemoMode refuses for a signed-in user who is not a superuser.
    if (current === DEMO_STATUS.OFF) enterDemoMode({ source: 'remote' });
    return;
  }
  if (current !== DEMO_STATUS.OFF) {
    store.commit('demoMode/SET_REMOTE_EXIT_IGNORED', true);
  }
}

export function initDemoMode() {
  if (initialized) return;
  initialized = true;
  setSpoofFailureHandler(onEngineFailure);
  setSensitiveHrefRewriter(tokenizeHref);
  setRouteTokenAccess(demoModeAllowedForCurrentUser);
  if (typeof window !== 'undefined') {
    window.addEventListener('storage', handleStorageEvent);
    window.addEventListener('pagehide', onPageHide);
    window.addEventListener('pageshow', onPageShow);
  }
  watchAccess();
  if (readPersistedDemoFlag()) enterDemoMode({ source: 'boot' });
  // Not protected at boot means no tab is protecting anywhere: tokens from an
  // earlier session can only be stale.
  else clearRouteTokens();
}

let accessScope = null;

// Re-checks access whenever the signed-in user changes (the session resolves
// at boot, or someone else logs in later in this tab), and clears everything
// when a logout starts. Detached scope: it must outlive whatever called init.
function watchAccess() {
  if (accessScope) return;
  accessScope = effectScope(true);
  accessScope.run(() => {
    store.watch(
      (state, getters) => {
        const user = getters['auth/current_user'];
        return [getters['auth/isAuthenticated'], user?.id ?? null, user?.is_superuser === true];
      },
      enforceDemoModeAccess,
      { flush: 'sync', immediate: true }
    );
  });
  unsubscribeLogout = store.subscribeAction({
    before: (action) => {
      if (action.type === 'auth/logout') revokeDemoMode();
    },
  });
}

let unsubscribeLogout = null;

export function resetDemoControllerForTests() {
  if (typeof window !== 'undefined') {
    window.removeEventListener('storage', handleStorageEvent);
    window.removeEventListener('pagehide', onPageHide);
    window.removeEventListener('pageshow', onPageShow);
  }
  dropLease();
  stopTitleGuard();
  accessScope?.stop();
  accessScope = null;
  unsubscribeLogout?.();
  unsubscribeLogout = null;
  activationStep = null;
  exitStep = null;
  setSensitiveHrefRewriter(null);
  setRouteTokenAccess(null);
  initialized = false;
  coveredCount = 0;
  setSpoofFailureHandler(null);
}
